// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import HomePage from '../screens/HomePage';
import LibraryPage from '../screens/LibraryPage';
import ProcedureViewPage from '../screens/ProcedureViewPage';
import AddProcedurePage from '../screens/AddProcedurePage';
import CreateProcedurePage from '../screens/CreateProcedurePage';
import CreatePolicyPage from '../screens/CreatePolicyPage';
import BulkImportPage from '../screens/BulkImportPage';
import ReviewQueuePage from '../screens/ReviewQueuePage';
import UsersPage from '../screens/UsersPage';
import UserDetailPage from '../screens/UserDetailPage';
import RolesPage from '../screens/RolesPage';
import PlaceholderPage from '../screens/PlaceholderPage';
import { FlagRoute } from '../../shared/featureFlags';

/**
 * Routes for everything under /admin-center. Every sidebar entry
 * (AdminSidebar) has a matching route here — unbuilt ones point at
 * PlaceholderPage rather than dead-ending. See admin-center/README.md
 * before adding a nav item without a route to match.
 *
 * This `<Routes>` is nested inside App.tsx's `<Route path="/admin-center/*">`,
 * so it matches against the pathname with that "/admin-center" prefix
 * already stripped (React Router resolves nested `<Routes>` relative to the
 * parent match, not against the full URL) — paths here must be relative to
 * the mount point, not repeat it. An absolute-looking path like
 * "/admin-center/rules" would never match anything except by accident, and
 * every navigation would silently fall through to the `*` redirect below.
 */
export default function AdminCenterRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="procedures/library" element={<LibraryPage />} />
      <Route path="procedures/view/:artifactId" element={<ProcedureViewPage />} />
      <Route
        path="procedures/add"
        element={
          <FlagRoute flag="addProcedureEnabled" fallback={<Navigate to="/admin-center" replace />}>
            <AddProcedurePage />
          </FlagRoute>
        }
      />
      <Route
        path="procedures/new"
        element={
          <FlagRoute flag="addProcedureEnabled" fallback={<Navigate to="/admin-center" replace />}>
            <CreateProcedurePage />
          </FlagRoute>
        }
      />
      <Route
        path="procedures/new-policy"
        element={
          <FlagRoute flag="addProcedureEnabled" fallback={<Navigate to="/admin-center" replace />}>
            <CreatePolicyPage />
          </FlagRoute>
        }
      />
      <Route
        path="procedures/bulk-import"
        element={
          <FlagRoute flag="bulkImportEnabled" fallback={<Navigate to="/admin-center" replace />}>
            <BulkImportPage />
          </FlagRoute>
        }
      />
      <Route
        path="procedures/review-queue"
        element={
          <FlagRoute flag="reviewQueueEnabled" fallback={<Navigate to="/admin-center" replace />}>
            <ReviewQueuePage />
          </FlagRoute>
        }
      />
      <Route path="rules" element={<PlaceholderPage title="All rules" />} />
      <Route
        path="rules/alarm-definitions"
        element={<PlaceholderPage title="Alarm definitions" />}
      />
      <Route
        path="users"
        element={
          <FlagRoute
            flag="accessManagementEnabled"
            fallback={<Navigate to="/admin-center" replace />}
          >
            <UsersPage />
          </FlagRoute>
        }
      />
      <Route
        path="users/:userId"
        element={
          <FlagRoute
            flag="accessManagementEnabled"
            fallback={<Navigate to="/admin-center" replace />}
          >
            <UserDetailPage />
          </FlagRoute>
        }
      />
      <Route
        path="roles"
        element={
          <FlagRoute
            flag="accessManagementEnabled"
            fallback={<Navigate to="/admin-center" replace />}
          >
            <RolesPage />
          </FlagRoute>
        }
      />
      <Route path="*" element={<Navigate to="/admin-center" replace />} />
    </Routes>
  );
}
